import type { AdminMoneyTimelineItem, AdminWalletTransaction } from '../api.js';
import {
  formatRatePercent,
  formatTimestamp,
  formatToman,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';
import { type ReactNode } from 'react';
import { Link } from 'react-router';

import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { DetailGrid, Dialog, Money, StatusChip } from '../kit.js';

interface WalletBuyerValue {
  readonly buyerId: string;
  readonly displayName: string;
  readonly mobileMasked: string;
}

export function WalletBuyer({ buyer }: { readonly buyer: WalletBuyerValue }) {
  return (
    <Link
      className="inline-flex flex-col"
      onClick={(event) => event.stopPropagation()}
      to={`/buyers/${encodeURIComponent(buyer.buyerId)}`}
    >
      <span>{buyer.displayName}</span>
      <bdi className="font-normal text-muted-foreground" dir="ltr">
        {buyer.mobileMasked}
      </bdi>
    </Link>
  );
}

/** A screen row. Built from the generated timeline item; not an API type. */
export interface TimelineView {
  readonly actor: string;
  readonly amountIrr: number | null;
  readonly buyer?: {
    readonly buyerId: string;
    readonly displayName: string;
    readonly mobileMasked: string;
  };
  readonly detail: Record<string, unknown>;
  readonly effect: string | null;
  readonly itemId: string;
  readonly kind: string;
  readonly occurredAt: string;
  readonly status: string;
}

const DETAIL_LABELS: Readonly<Record<string, string>> = {
  action: 'کار',
  currentPrincipalIrr: 'مبلغ خرید',
  feeIrr: 'کارمزد',
  feeRate: 'نرخ کارمزد',
  lateFeeIrr: 'جریمه دیرکرد',
  multiplier: 'ضریب اعتبار',
  paymentFeeIrr: 'کارمزد پرداخت',
  paymentType: 'نوع خرید',
  previousMultiplier: 'ضریب قبلی',
  principalIrr: 'اصل',
  provider: 'درگاه',
  reason: 'دلیل',
  requestReference: 'شماره درخواست',
  saleReference: 'شماره خرید',
  source: 'منبع پرداخت',
  totalIrr: 'مبلغ پرداختی',
};

function text(value: unknown): string | undefined {
  if (typeof value === 'string' && value !== '') return value;
  if (typeof value === 'number') return toPersianDigits(value);
  return undefined;
}

function actorLabel(actor: AdminMoneyTimelineItem['actor']): string {
  const who = statusLabel('moneyActorType', actor.type);
  if (actor.displayName !== null && actor.displayName !== '')
    return `${who} · ${actor.displayName}`;
  if (actor.jobName !== null && actor.jobName !== '') return `${who} · ${actor.jobName}`;
  return who;
}

function effectLabel(effect: AdminMoneyTimelineItem['walletEffect']): string | null {
  if (effect === null) return null;
  const parts = [
    effect.freeDeltaIrr === 0 ? null : `آزاد ${formatToman(effect.freeDeltaIrr)}`,
    effect.heldDeltaIrr === 0 ? null : `در انتظار تأیید ${formatToman(effect.heldDeltaIrr)}`,
    effect.frozenDeltaIrr === 0 ? null : `بلوکه ${formatToman(effect.frozenDeltaIrr)}`,
    effect.withdrawalPendingDeltaIrr === 0
      ? null
      : `در حال برداشت ${formatToman(effect.withdrawalPendingDeltaIrr)}`,
  ].filter((part) => part !== null);
  return parts.length === 0 ? 'بدون تغییر مانده' : parts.join('، ');
}

function detailOf(item: AdminMoneyTimelineItem): Record<string, unknown> {
  switch (item.kind) {
    case 'PURCHASE':
      return item.purchase;
    case 'INSTALLMENT_PAYMENT':
      return item.payment;
    case 'TOP_UP':
      return item.topUp;
    case 'WITHDRAWAL':
      return item.withdrawal;
    case 'TRANSFER_SENT':
    case 'TRANSFER_RECEIVED':
      return item.transfer;
    case 'REFUND':
      return item.refund;
    case 'WALLET_FREEZE':
      return item.freeze;
    case 'WALLET_RELEASE':
      return item.release;
    case 'MULTIPLIER_OVERRIDE':
      return item.override;
    default:
      return {};
  }
}

export function timelineView(item: AdminMoneyTimelineItem): TimelineView {
  return {
    actor: actorLabel(item.actor),
    amountIrr: item.amountIrr,
    detail: detailOf(item),
    effect: effectLabel(item.walletEffect),
    itemId: item.itemId,
    kind: item.kind,
    occurredAt: item.occurredAt,
    status: item.status,
  };
}

/**
 * openapi-typescript replaces the timeline discriminator on `AdminWalletTransaction`.
 * The response still carries the timeline `kind` and its detail.
 */
export function transactionView(item: AdminWalletTransaction): TimelineView {
  const timeline = item as unknown as AdminMoneyTimelineItem;
  return { ...timelineView(timeline), buyer: item.buyer };
}

/** Withdrawal statuses have no shared dictionary entry yet; keep the Persian labels here. */
export function withdrawalStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'در انتظار';
    case 'PROCESSING':
      return 'در حال پردازش';
    case 'SUCCEEDED':
      return 'موفق';
    case 'FAILED':
      return 'ناموفق';
    case 'REVERSED':
      return 'برگشت‌خورده';
    case 'CANCELLED':
      return 'لغوشده';
    default:
      return statusLabel('saleStatus', status);
  }
}

export function timelineStatus(kind: string, status: string): ReactNode {
  if (kind === 'PURCHASE') return <StatusChip enumName="saleStatus" value={status} />;
  if (kind === 'TOP_UP') return <StatusChip enumName="walletTopUpStatus" value={status} />;
  if (kind === 'INSTALLMENT_PAYMENT') {
    return <StatusChip enumName="paymentAttemptStatus" value={status} />;
  }
  if (kind === 'WITHDRAWAL') return withdrawalStatusLabel(status);
  if (status === 'POSTED') return 'ثبت‌شده';
  if (status === 'CREDITED_TO_WALLET') return 'واریز به کیف پول';
  return statusLabel('saleStatus', status);
}

function detailValue(key: string, value: unknown): ReactNode {
  if (key === 'action' && typeof value === 'string') {
    if (value === 'SET') return 'ثبت ضریب اختصاصی';
    if (value === 'REMOVE') return 'حذف ضریب اختصاصی';
    return 'نامشخص';
  }
  if (key === 'paymentType' && typeof value === 'string') {
    return statusLabel('salePaymentType', value);
  }
  if (key === 'source' && typeof value === 'string') {
    return statusLabel('moneyTimelinePaymentSource', value);
  }
  if (key === 'provider' && typeof value === 'string') {
    return statusLabel('walletProvider', value);
  }
  if (key === 'feeRate' && typeof value === 'string') {
    return formatRatePercent(value, { invalid: 'raw' });
  }
  if ((key === 'multiplier' || key === 'previousMultiplier') && typeof value === 'string') {
    const [whole = value, fraction = ''] = value.split('.');
    const trimmed = fraction.replace(/0+$/, '');
    return toPersianDigits(trimmed === '' ? whole : `${whole}.${trimmed}`);
  }
  if ((key.endsWith('Irr') || key === 'amountIrr') && typeof value === 'number') {
    return <Money rials={value} />;
  }
  if (key === 'merchant' && value !== null && typeof value === 'object' && 'nameFa' in value) {
    const name = (value as { nameFa?: unknown }).nameFa;
    return typeof name === 'string' ? name : '—';
  }
  return text(value) ?? '—';
}

export function TimelineDetail({
  onClose,
  row,
}: {
  readonly onClose: () => void;
  readonly row: TimelineView | null;
}) {
  const facts =
    row === null
      ? []
      : [
          { key: 'kind', label: 'نوع', value: statusLabel('moneyTimelineKind', row.kind) },
          { key: 'when', label: 'زمان', value: formatTimestamp(row.occurredAt) },
          { key: 'actor', label: 'انجام‌دهنده', value: row.actor },
          {
            key: 'amount',
            label: 'مبلغ',
            value: row.amountIrr === null ? '—' : <Money rials={row.amountIrr} />,
          },
          ...(row.effect === null
            ? []
            : [{ key: 'effect', label: 'اثر روی کیف پول', value: row.effect }]),
          ...(row.buyer === undefined
            ? []
            : [
                {
                  key: 'buyer',
                  label: 'خریدار',
                  value: <WalletBuyer buyer={row.buyer} />,
                },
              ]),
          ...Object.entries(DETAIL_LABELS).flatMap(([key, label]) => {
            const value = row.detail[key];
            if (value === undefined || value === null) return [];
            return [{ key, label, value: detailValue(key, value) }];
          }),
        ];
  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      open={row !== null}
      title="جزئیات"
    >
      {row === null ? null : (
        <>
          <p>{timelineStatus(row.kind, row.status)}</p>
          <DetailGrid items={facts} />
        </>
      )}
    </Dialog>
  );
}

export function TimelineTable({
  caption,
  onOpen,
  rows,
  showBuyer,
}: {
  readonly caption: string;
  readonly onOpen: (row: TimelineView) => void;
  readonly rows: readonly TimelineView[];
  readonly showBuyer: boolean;
}) {
  const columns: DataTableColumn<TimelineView>[] = [
    ...(showBuyer
      ? [
          {
            header: 'خریدار',
            id: 'buyer',
            pin: 'start' as const,
            render: (row: TimelineView) =>
              row.buyer === undefined ? '—' : <WalletBuyer buyer={row.buyer} />,
          },
        ]
      : []),
    {
      header: 'نوع',
      id: 'kind',
      render: (row) => statusLabel('moneyTimelineKind', row.kind),
    },
    {
      header: 'مبلغ',
      id: 'amount',
      numeric: true,
      render: (row) => (row.amountIrr === null ? '—' : <Money rials={row.amountIrr} />),
    },
    {
      header: 'وضعیت',
      id: 'status',
      render: (row) => timelineStatus(row.kind, row.status),
    },
    {
      header: 'زمان',
      id: 'when',
      render: (row) => formatTimestamp(row.occurredAt),
    },
  ];
  return (
    <AdminListTable
      caption={caption}
      columns={columns}
      mobileSummary={(row) => ({
        primary: statusLabel('moneyTimelineKind', row.kind),
        secondary: row.amountIrr === null ? '—' : formatToman(row.amountIrr),
      })}
      onRowClick={onOpen}
      rowKey={(row) => row.itemId}
      rows={rows}
    />
  );
}
